import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Link, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SwitchField from '../common/SwitchField';
import StoreStripeConnectOnboardingCard from './StoreStripeConnectOnboardingCard';
import StorePaymentMethodFeeDialog, { PAYMENT_METHOD_FEES } from './StorePaymentMethodFeeDialog';
import StoreStatementDescriptorSection from './StoreStatementDescriptorSection';
import StoreService from '../../services/storeService';
import useStripeConnectOnboarding from '../../hooks/useStripeConnectOnboarding';
import useStorePlan from '../../hooks/useStorePlan';
import { useToast } from '../../context/ToastContext';
import { CustomerPaymentMode, normalizeCustomerPaymentMode } from '../../constants/CustomerPaymentMode';
import { isApiErrorCode } from '../../utils/apiErrorCode';
import { BRAND } from '../../theme/brandTokens';

const sectionContentPadding = { xs: 2.5, sm: 3, md: 4 };
const stepHeadingSx = { fontWeight: 600, color: BRAND.primaryText, mb: 1 };

const PAYMENT_METHOD_LABEL_KEYS = {
  Card: 'paymentSettingsPage.customerPayments.methods.card',
  Klarna: 'paymentSettingsPage.customerPayments.methods.klarna',
  MbWay: 'paymentSettingsPage.customerPayments.methods.mbWay',
  Pix: 'paymentSettingsPage.customerPayments.methods.pix',
  Scalapay: 'paymentSettingsPage.customerPayments.methods.scalapay',
  Sequra: 'paymentSettingsPage.customerPayments.methods.sequra',
};

const normalizePaymentMethods = (methods) => {
  if (!Array.isArray(methods)) {
    return [];
  }

  return methods.map((method) => ({
    type: method?.type ?? method?.Type,
    isActive: Boolean(method?.isActive ?? method?.IsActive),
    isEnabled: Boolean(method?.isEnabled ?? method?.IsEnabled),
  })).filter((method) => Boolean(method.type));
};

const normalizeSettings = (result) => ({
  appointmentCustomerPaymentMode: normalizeCustomerPaymentMode(
    result?.appointmentCustomerPaymentMode ?? result?.AppointmentCustomerPaymentMode
  ),
  membershipCustomerPaymentMode: normalizeCustomerPaymentMode(
    result?.membershipCustomerPaymentMode ?? result?.MembershipCustomerPaymentMode
  ),
  servicePackageCustomerPaymentMode: normalizeCustomerPaymentMode(
    result?.servicePackageCustomerPaymentMode ?? result?.ServicePackageCustomerPaymentMode
  ),
  stripePaymentReady: Boolean(result?.stripePaymentReady ?? result?.StripePaymentReady),
  paymentMethods: normalizePaymentMethods(result?.paymentMethods ?? result?.PaymentMethods),
});

const StoreCustomerPaymentsPanel = ({ storeId, isOwner }) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const {
    onboardingStatus,
    loading: onboardingLoading,
    actionLoading,
    isComplete: onboardingComplete,
    startOnboarding,
    refreshOnboardingStatus,
  } = useStripeConnectOnboarding(storeId, { enabled: isOwner });
  const { entitlements } = useStorePlan(storeId);
  const allowsServicePackages = Boolean(entitlements?.allowsServicePackages);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feeMethodType, setFeeMethodType] = useState(null);

  const stripePaymentReady = Boolean(settings?.stripePaymentReady);

  const loadSettings = useCallback(async () => {
    if (!storeId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await StoreService.getPaymentSettings(storeId);
      setSettings(normalizeSettings(result));
    } catch (err) {
      console.error('Failed to load customer payment settings:', err);
      showError(t('paymentSettingsPage.customerPayments.error.load'));
    } finally {
      setLoading(false);
    }
  }, [showError, storeId, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings, onboardingComplete]);

  const persist = async (nextSettings) => {
    try {
      setSaving(true);
      await StoreService.updatePaymentSettings(storeId, {
        appointmentCustomerPaymentMode: nextSettings.appointmentCustomerPaymentMode,
        membershipCustomerPaymentMode: nextSettings.membershipCustomerPaymentMode,
        servicePackageCustomerPaymentMode: nextSettings.servicePackageCustomerPaymentMode,
        enabledPaymentMethodTypes: nextSettings.paymentMethods
          .filter((method) => method.isEnabled)
          .map((method) => method.type),
      });
      setSettings(nextSettings);
      showSuccess(t('paymentSettingsPage.customerPayments.success.save'));
    } catch (err) {
      console.error('Failed to save customer payment settings:', err);
      showError(isApiErrorCode(err, 'StorePrepaymentRequiresOnlinePayments')
        ? t('paymentSettingsPage.customerPayments.error.prepaymentServices')
        : (err?.response?.data?.message || t('paymentSettingsPage.customerPayments.error.save')));
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePaymentMode = (field) => (event) => {
    if (!settings) {
      return;
    }
    const nextValue = event.target.checked ? CustomerPaymentMode.Both : CustomerPaymentMode.InStoreOnly;
    persist({ ...settings, [field]: nextValue });
  };

  const handleTogglePaymentMethod = (type) => (event) => {
    if (!settings || type === 'Card') {
      return;
    }
    const nextMethods = settings.paymentMethods.map((method) => (
      method.type === type ? { ...method, isEnabled: event.target.checked } : method
    ));
    persist({ ...settings, paymentMethods: nextMethods });
  };

  if (loading) {
    return (
      <Box sx={{ p: sectionContentPadding, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: BRAND.accent }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: sectionContentPadding, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h6" sx={stepHeadingSx}>
          {t('paymentSettingsPage.customerPayments.step1.title')}
        </Typography>
        {onboardingComplete ? (
          <>
            <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success">
              {t('paymentSettingsPage.customerPayments.step1.ready')}
            </Alert>
            {/* Stripe can enable a payment method days after onboarding, so the owner needs a
                way back into Stripe and a way to re-check even once this reads as connected. */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={actionLoading}
                onClick={() => refreshOnboardingStatus().then(loadSettings)}
                sx={{ textTransform: 'none', fontWeight: 600, color: BRAND.accentText, borderColor: BRAND.line }}
              >
                {t('paymentSettingsPage.customerPayments.step1.refresh')}
              </Button>
              <Button
                size="small"
                variant="text"
                disabled={actionLoading}
                onClick={startOnboarding}
                sx={{ textTransform: 'none', fontWeight: 600, color: BRAND.accentText }}
              >
                {t('paymentSettingsPage.customerPayments.step1.manageOnStripe')}
              </Button>
            </Box>
          </>
        ) : (
          <StoreStripeConnectOnboardingCard
            onboardingStatus={onboardingStatus}
            loading={onboardingLoading}
            actionLoading={actionLoading}
            isComplete={onboardingComplete}
            onStartOnboarding={startOnboarding}
            onRefreshStatus={() => refreshOnboardingStatus().then(loadSettings)}
          />
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="h6" sx={stepHeadingSx}>
          {t('paymentSettingsPage.customerPayments.step2.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('paymentSettingsPage.customerPayments.step2.description')}
        </Typography>
        {settings?.paymentMethods?.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {settings.paymentMethods.map((method) => (
              <Box key={method.type} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <SwitchField
                  label={t(PAYMENT_METHOD_LABEL_KEYS[method.type] || method.type)}
                  checked={method.type === 'Card' ? true : method.isEnabled}
                  onChange={handleTogglePaymentMethod(method.type)}
                  disabled={saving || method.type === 'Card'}
                  compact
                />
                {PAYMENT_METHOD_FEES[method.type] ? (
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => setFeeMethodType(method.type)}
                    sx={{ color: BRAND.accentText, textDecorationColor: BRAND.line }}
                  >
                    {t('paymentSettingsPage.customerPayments.fees.checkFee')}
                  </Link>
                ) : null}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('paymentSettingsPage.customerPayments.step2.empty')}
          </Typography>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="h6" sx={stepHeadingSx}>
          {t('paymentSettingsPage.customerPayments.step3.title')}
        </Typography>
        {!stripePaymentReady ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {t('paymentSettingsPage.customerPayments.step3.needsOnboarding')}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <SwitchField
            label={t('paymentSettingsPage.customerPayments.step3.services')}
            helpText={t('paymentSettingsPage.customerPayments.step3.servicesHelp')}
            checked={settings?.appointmentCustomerPaymentMode === CustomerPaymentMode.Both}
            onChange={handleTogglePaymentMode('appointmentCustomerPaymentMode')}
            disabled={saving || !stripePaymentReady}
          />
          <SwitchField
            label={t('paymentSettingsPage.customerPayments.step3.memberships')}
            checked={settings?.membershipCustomerPaymentMode === CustomerPaymentMode.Both}
            onChange={handleTogglePaymentMode('membershipCustomerPaymentMode')}
            disabled={saving || !stripePaymentReady}
          />
          {allowsServicePackages ? (
            <SwitchField
              id="payment-settings-service-packages-online"
              label={t('paymentSettingsPage.customerPayments.step3.packages')}
              checked={settings?.servicePackageCustomerPaymentMode === CustomerPaymentMode.Both}
              onChange={handleTogglePaymentMode('servicePackageCustomerPaymentMode')}
              disabled={saving || !stripePaymentReady}
            />
          ) : null}
        </Box>
      </Box>

      {stripePaymentReady ? <StoreStatementDescriptorSection storeId={storeId} /> : null}

      <StorePaymentMethodFeeDialog
        open={Boolean(feeMethodType)}
        onClose={() => setFeeMethodType(null)}
        methodType={feeMethodType}
        methodLabel={feeMethodType ? t(PAYMENT_METHOD_LABEL_KEYS[feeMethodType] || feeMethodType) : ''}
      />
    </Box>
  );
};

export default StoreCustomerPaymentsPanel;
